import { call, put, takeEvery } from 'redux-saga/effects'
import { FetchPublicOpportunity, SubmitOpportunityApplication } from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { PublicOpportunity, SubmitApplicationParams } from 'src/interfaces/opportunity'
import { getApiErrorMessage } from 'src/utils/errorHandlers'
import { AsyncAction } from 'src/utils/reduxUtils'
import { captureSentryError } from 'src/utils/sentry'
import { ApiService, callApi } from './api/callApi'

interface FetchPublicOpportunityParams {
  publicToken: string
}

/**
 * Unauthenticated on purpose: the opaque token in the URL is the only thing identifying the posting, so these use
 * callApi rather than one of the secure variants.
 */
function* onFetchPublicOpportunity(
  action: AsyncAction<FetchPublicOpportunityParams, PublicOpportunity>,
) {
  try {
    const { payload }: { payload: PublicOpportunity } = yield call(
      callApi,
      Endpoints.FetchPublicOpportunity,
      {
        method: 'GET',
        apiService: ApiService.Paraworks,
        params: [{ field: ':publicToken', value: encodeURIComponent(action.payload.publicToken) }],
      },
    )

    yield put(FetchPublicOpportunity.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.opportunity.onFetchPublicOpportunity] error',
    })

    yield put(FetchPublicOpportunity.failed(err))

    if (action.next) {
      action.next(getApiErrorMessage(err, 'Could not load this opportunity'))
    }
  }
}

function* onSubmitOpportunityApplication(
  action: AsyncAction<SubmitApplicationParams, { submitted: boolean }>,
) {
  const { publicToken, ...body } = action.payload

  try {
    const { payload }: { payload: { submitted: boolean } } = yield call(
      callApi,
      Endpoints.SubmitOpportunityApplication,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body,
        params: [{ field: ':publicToken', value: encodeURIComponent(publicToken) }],
      },
    )

    yield put(SubmitOpportunityApplication.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.opportunity.onSubmitOpportunityApplication] error',
    })

    yield put(SubmitOpportunityApplication.failed(err))

    if (action.next) {
      action.next(getApiErrorMessage(err, 'Could not submit your application'))
    }
  }
}

export function* opportunitySaga() {
  yield takeEvery(FetchPublicOpportunity.type.REQUEST, onFetchPublicOpportunity)
  yield takeEvery(SubmitOpportunityApplication.type.REQUEST, onSubmitOpportunityApplication)
}
